import { CreateEditFolderDto, LinkFilesDto } from '@geartracker/dto';
import type { Folder } from '@geartracker/types';

import { enqueueSnackbar } from 'src/components/snackbar';
import { useTranslate } from 'src/locales/use-locales';
import axios, { endpoints } from 'src/utils/axios';
import { getErrorMessage } from 'src/utils/error-message';
import { assertDtoShape } from 'src/utils/validate-dto';
import { equipmentKeys } from './equipment.api';
import { eventKeys } from './events.api';
import { statsKeys } from './stats.api';
import { transformFolderWithCount } from './transform-api-data.api';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const folderKeys = {
  all: ['folders'] as const,
  lists: () => [...folderKeys.all, 'list'] as const,
  details: () => [...folderKeys.all, 'detail'] as const,
  detail: (id: number) => [...folderKeys.details(), id] as const,
};

//FOLDERS LIST
const getFolders = async (): Promise<Folder[]> => {
  const response = await axios.get(endpoints.folder.root());
  return response.data.map(transformFolderWithCount);
};

export const useFolders = () => {
  return useQuery({
    queryKey: folderKeys.lists(),
    queryFn: getFolders,
  });
};

//FOLDER DETAILS
const getFolder = async (id: number): Promise<Folder> => {
  const response = await axios.get(endpoints.folder.root(id));
  return transformFolderWithCount(response.data);
};

export const useFolder = (id: number, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: folderKeys.detail(id),
    queryFn: () => getFolder(id),
    enabled: options?.enabled !== false,
  });
};

//CREATE FOLDER
const createFolder = async (folder: CreateEditFolderDto): Promise<Folder> => {
  const response = await axios.post(
    endpoints.folder.root(),
    assertDtoShape(CreateEditFolderDto, folder, 'createFolder'),
  );
  return transformFolderWithCount(response.data);
};

export const useCreateFolder = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslate('api.folder.create');

  return useMutation({
    mutationFn: createFolder,
    onSuccess: () => {
      enqueueSnackbar(t('success'));
      void queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: statsKeys.all });
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t('error', { error: errorMessage }), {
        variant: 'error',
      });
    },
  });
};

//UPDATE FOLDER
export const updateFolder = async ({
  folderId,
  folderDto,
}: {
  folderId: number;
  folderDto: CreateEditFolderDto;
}): Promise<Folder> => {
  const response = await axios.patch(
    endpoints.folder.root(folderId),
    assertDtoShape(CreateEditFolderDto, folderDto, 'updateFolder'),
  );
  return transformFolderWithCount(response.data);
};

export const useUpdateFolder = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslate('api.folder.update');

  return useMutation({
    mutationFn: updateFolder,
    onSuccess: (data) => {
      enqueueSnackbar(t('success'));
      void queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: folderKeys.detail(data.id),
      });
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t('error', { error: errorMessage }), {
        variant: 'error',
      });
    },
  });
};

//DELETE FOLDER
const deleteFolder = async (id: number): Promise<void> => {
  await axios.delete(endpoints.folder.root(id));
};

export const useDeleteFolder = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslate('api.folder.delete');

  return useMutation({
    mutationFn: deleteFolder,
    onSuccess: () => {
      enqueueSnackbar(t('success'));
      void queryClient.invalidateQueries({ queryKey: folderKeys.all });
      void queryClient.invalidateQueries({ queryKey: equipmentKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: statsKeys.all });
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t('error', { error: errorMessage }), {
        variant: 'error',
      });
    },
  });
};

//ADD FILE
const linkFiles = async ({
  folderId,
  linkFilesDto,
}: {
  folderId: number;
  linkFilesDto: LinkFilesDto;
}): Promise<Folder> => {
  return transformFolderWithCount(
    (
      await axios.post(
        endpoints.folder.linkFiles(folderId),
        assertDtoShape(LinkFilesDto, linkFilesDto, 'linkFiles'),
      )
    ).data,
  );
};

export const useLinkFilesToFolder = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslate('api.folder.addFiles');

  return useMutation({
    mutationFn: linkFiles,
    onSuccess: (updatedFolder) => {
      enqueueSnackbar(t('success'));

      queryClient.setQueryData<Folder>(
        folderKeys.detail(updatedFolder.id),
        (_oldFolder) => {
          return updatedFolder;
        },
      );
      queryClient.setQueryData<Folder[]>(folderKeys.lists(), (oldData) => {
        if (!oldData) return []; //should never happen
        return oldData.map((folder) => {
          if (folder.id === updatedFolder.id) {
            return updatedFolder;
          }
          return folder;
        });
      });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t('error', { error: errorMessage }), {
        variant: 'error',
      });
    },
  });
};

// REMOVE FILE
const deleteFile = async ({
  folderId,
  fileId,
}: {
  folderId: number;
  fileId: number;
}): Promise<Folder> => {
  return transformFolderWithCount(
    (await axios.delete(endpoints.folder.removeFile(folderId, fileId))).data,
  );
};

export const useDeleteFileFromFolder = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslate('api.folder.removeFile');

  return useMutation({
    mutationFn: deleteFile,
    onSuccess: (updatedFolder) => {
      enqueueSnackbar(t('success'));
      queryClient.setQueryData<Folder>(
        folderKeys.detail(updatedFolder.id),
        (_oldData) => {
          return updatedFolder;
        },
      );
      queryClient.setQueryData<Folder[]>(folderKeys.lists(), (oldData) => {
        if (!oldData) return []; //should never happen
        return oldData.map((folder) => {
          if (folder.id === updatedFolder.id) {
            return updatedFolder;
          }
          return folder;
        });
      });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t('error', { error: errorMessage }), {
        variant: 'error',
      });
    },
  });
};
